import { baseApi } from "./baseApi";
import {
  parseMutualFundsErrorResponse,
  parseMutualFundsResponse,
  parseMutualFundByIdResponse,
  parseMutualFundHoldingsResponse,
  parseMutualFundHistoryResponse,
  parseMutualFundCollectionsResponse,
  parsePopularFundsResponse,
  parseRiskProfileResponse,
  parseRecommendationFundsResponse,
  parseManagerListResponse,
  parseAmcListResponse,
  parseCategoryListResponse,
  parseSwpParamsResponse,
} from "../parsers/mutualFunds.parsers";
import {
  parseRiskReturnMatrixResponse,
  parseFundCardErrorResponse,
  parseHoldingsResponse,
  parseMetricsOverviewResponse,
  parseEquityAnalysisResponse,
  parseRiskRatiosResponse,
  parseRiskAdjustedReturnsResponse,
  parseRiskReturnStatisticsResponse,
  parseSectorAllocationResponse,
  parseRollingAlphaResponse,
  parseAssetAllocationResponse,
  parseFundManagersHistoryResponse,
} from "../parsers/fundCard.parsers";

export const mutualFundsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMutualFunds: builder.mutation({
      query: (payload) => ({
        url: "/api/mutual-funds/list",
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => {
        return parseMutualFundsResponse(response);
      },
    }),

    getMutualFundById: builder.query({
      query: (fundId) => ({
        url: `/api/mutual-funds/${fundId}`,
        method: "GET",
      }),
      transformResponse: (response) => {
        return parseMutualFundByIdResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseMutualFundsErrorResponse(error);
      },
      providesTags: ["MutualFundsDetails"],
    }),

    getMutualFundHoldings: builder.query({
      query: (fundId) => ({
        url: `/api/mutual-funds/${fundId}/holdings`,
        method: "GET",
      }),
      transformResponse: (response) => {
        return parseMutualFundHoldingsResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseMutualFundsErrorResponse(error);
      },
      providesTags: ["MutualFundsHoldings"],
    }),

    getMutualFundHistory: builder.query({
      query: ({ fundId, tenure }) => ({
        url: `/api/mutual-funds/${fundId}/history?tenure=${tenure}`,
        method: "GET",
      }),
      transformResponse: (response) => {
        return parseMutualFundHistoryResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseMutualFundsErrorResponse(error);
      },
      providesTags: ["MutualFundsHistory"],
    }),

    getMutualFundCollections: builder.query({
      query: () => ({
        url: "/api/mutual-funds/collections",
        method: "POST",
        body: {},
      }),
      transformResponse: parseMutualFundCollectionsResponse,
      providesTags: ["MutualFunds"],
    }),

    /**
     * Get popular funds
     * POST /api/mutual-funds/popular-funds
     *
     * @purpose Retrieve popular mutual funds
     * @useCase Display popular funds in dashboard/homepage with category filtering
     *
     * @request {Object} payload - Empty object (no parameters required)
     *
     * @response {Object}
     * @response {string} code - Response code (e.g., "MFDS0001")
     * @response {number} statusCode - HTTP status code (200)
     * @response {string} message - Success message
     * @response {Object} result - Response data
     * @response {Object} result.data - Object with category keys (equity, debt, hybrid)
     * @response {Array} result.data.equity - Array of equity funds
     * @response {Array} result.data.debt - Array of debt funds
     * @response {Array} result.data.hybrid - Array of hybrid funds
     * @response {string} requestId - Request ID
     */
    getPopularFunds: builder.query({
      query: (params = {}) => ({
        url: "/api/mutual-funds/popular-funds",
        method: "POST",
        body: {},
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response) => {
        try {
          return parsePopularFundsResponse(response);
        } catch (error) {
          console.error("Error parsing popular funds response:", error);
          return {
            success: false,
            message: "Error parsing response",
            code: "PARSE_ERROR",
            funds: {},
            requestId: null,
          };
        }
      },
      transformErrorResponse: (error) => parseMutualFundsErrorResponse(error),
      providesTags: ["MutualFunds"],
    }),

    // getMutualFundHoldings: builder.query({
    //   query: (fundId) => ({
    //     url: `/api/mutual-funds/${fundId}/holdings`,
    //     method: "GET",
    //   }),
    //   transformResponse: (response) => {
    //     return parseMutualFundHoldingsResponse(response);
    //   },
    //   transformErrorResponse: (error) => {
    //     return parseMutualFundsErrorResponse(error);
    //   },
    //   providesTags: ["MutualFundsHoldings"],
    // }),

    // getMutualFundHistory: builder.query({
    //   query: (fundId) => ({
    //     url: `/api/mutual-funds/${fundId}/history`,
    //     method: "GET",
    //   }),
    //   transformResponse: (response) => {
    //     return parseMutualFundHistoryResponse(response);
    //   },
    //   transformErrorResponse: (error) => {
    //     return parseMutualFundsErrorResponse(error);
    //   },
    //   providesTags: ["MutualFundsHistory"],
    // }),

    /**
     * Get risk profile funds
     * POST /api/mutual-funds/risk-profile
     *
     * @purpose Get mutual funds based on user's risk profile
     * @useCase Retrieve personalized fund recommendations based on risk assessment
     *
     * @request {Object} payload
     * @request {Object} payload.paginationParams - Pagination parameters
     * @request {number} payload.paginationParams.page - Page number (default: 1)
     * @request {number} payload.paginationParams.limit - Items per page (default: 10)
     *
     * @response {Object}
     * @response {string} code - Response code (e.g., "MFDS0003")
     * @response {string} message - Success message
     * @response {Object} result - Response data
     * @response {Array} result.data - Array of fund objects
     * @response {Object} result.pagination - Pagination information
     */
    getRiskProfileFunds: builder.mutation({
      query: (payload) => ({
        url: "/api/mutual-funds/risk-profile",
        method: "POST",
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response) => {
        return parseRiskProfileResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseMutualFundsErrorResponse(error);
      },
      providesTags: ["MutualFunds"],
    }),

    /**
     * Get recommendation funds
     * POST /api/mutual-funds/recommendation-funds
     *
     * @purpose Get recommended mutual funds for the user
     * @useCase Display recommended funds in the explore page after risk assessment
     *
     * @request {Object} payload - Empty object (no parameters required)
     *
     * @response {Object}
     * @response {string} code - Response code (e.g., "MFDS0008")
     * @response {number} statusCode - HTTP status code (200)
     * @response {string} message - Success message
     * @response {Object} result - Response data
     * @response {Array} result.data - Array of recommended fund objects
     * @response {string} requestId - Request ID
     */
    getRecommendationFunds: builder.mutation({
      query: () => ({
        url: "/api/mutual-funds/recommendation-funds",
        method: "POST",
        body: {},
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response) => {
        try {
          return parseRecommendationFundsResponse(response);
        } catch (error) {
          console.error("Error parsing recommendation funds response:", error);
          return {
            success: false,
            message: "Error parsing response",
            code: "PARSE_ERROR",
            funds: [],
            requestId: null,
          };
        }
      },
      transformErrorResponse: (error) => {
        return parseMutualFundsErrorResponse(error);
      },
      providesTags: ["MutualFunds"],
    }),

    /**
     * Confirm BSE onboarding
     * POST /api/mutual-funds/bse-onboarding/confirm
     *
     * @purpose Confirm BSE onboarding after OTP verification
     * @useCase Complete account setup after nominee opt-out OTP verification
     *
     * @request {Object} payload
     * @request {string} payload.actionRequestId - Action request ID from OTP verification
     *
     * @response {Object}
     * @response {string} code - Response code
     * @response {string} message - Success message
     * @response {Object} result - Response data
     */
    confirmBseOnboarding: builder.mutation({
      query: (payload) => ({
        url: "/api/mutual-funds/bse-onboarding/confirm",
        method: "POST",
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response) => {
        return {
          success: response?.success !== false,
          message: response?.message || "BSE onboarding confirmed successfully",
          code: response?.code,
          data: response?.result || response?.data || null,
          requestId: response?.requestId || null,
        };
      },
      transformErrorResponse: (error) => {
        return parseMutualFundsErrorResponse(error);
      },
      invalidatesTags: ["MutualFunds"],
    }),

    /**
     * Get risk-return matrix
     * POST /api/fund-card/risk-return-matrix
     *
     * @purpose Retrieve risk-return matrix data for all funds
     * @useCase Display risk-return bubble chart with selected fund highlighted
     *
     * @request {Object} payload
     * @request {string} payload.fundId - Fund ID to include in the request
     *
     * @response {Object}
     * @response {string} code - Response code
     * @response {string} message - Success message
     * @response {Object} result - Response data
     * @response {Array} result.funds - Array of funds with risk, return, size, and fundId
     */
    getRiskReturnMatrix: builder.mutation({
      query: (payload) => ({
        url: "/api/fund-card/risk-return-matrix",
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => {
        return parseRiskReturnMatrixResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseFundCardErrorResponse(error);
      },
      providesTags: ["MutualFunds"],
    }),
    /**
     * Get sector allocation (Top Sectors)
     * POST /api/fund-card/sector-allocation
     */
    getSectorAllocation: builder.mutation({
      query: (payload) => ({
        url: `/api/fund-card/sector-allocation`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => {
        return parseSectorAllocationResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseFundCardErrorResponse(error);
      },
      providesTags: ["MutualFunds"],
    }),

    /**
     * Get holdings
     * GET /api/fund-card/holdings
     *
     * @purpose Retrieve holdings data for a specific fund
     * @useCase Display holdings information in fund details page
     *
     * @request {Object} payload
     * @request {string} payload.fundId - Fund ID
     *
     * @response {Object}
     * @response {string} code - Response code
     * @response {string} message - Success message
     * @response {Object} result - Response data
     * @response {Array} result.holdings - Array of holdings with stock details
     */
    getHoldings: builder.mutation({
      query: (payload) => ({
        url: `/api/fund-card/holdings`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => {
        return parseHoldingsResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseFundCardErrorResponse(error);
      },
      providesTags: ["MutualFunds"],
    }),

    /**
     * Get risk return statistics
     * POST /api/fund-card/risk-return-statistics
     *
     * @purpose Retrieve risk return statistics for a specific fund
     * @useCase Display risk return statistics in StatsContainer
     *
     * @request {Object} payload
     * @request {string} payload.fundId - Fund ID
     *
     * @response {Object}
     * @response {string} code - Response code
     * @response {string} message - Success message
     * @response {Object} result - Response data
     * @response {Array} result.ratios - Array of risk return statistics
     */
    getRiskReturnStatistics: builder.mutation({
      query: (payload) => ({
        url: `/api/fund-card/risk-return-statistics`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => {
        return parseRiskReturnStatisticsResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseFundCardErrorResponse(error);
      },
      providesTags: ["MutualFunds"],
    }),
    metricsOverview: builder.mutation({
      query: (payload) => ({
        url: `/api/fund-card/metrics-overview`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => {
        return parseMetricsOverviewResponse(response);
      },
    }),

    equityAnalysis: builder.mutation({
      query: (payload) => ({
        url: `/api/fund-card/equity-analysis`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => {
        return parseEquityAnalysisResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseFundCardErrorResponse(error);
      },
    }),

    riskRatios: builder.mutation({
      query: (payload) => ({
        url: `/api/fund-card/risk-ratio`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => {
        return parseRiskRatiosResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseFundCardErrorResponse(error);
      },
    }),

    riskAdjustedReturns: builder.mutation({
      query: (payload) => ({
        url: `/api/fund-card/risk-adjusted-returns`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => {
        return parseRiskAdjustedReturnsResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseFundCardErrorResponse(error);
      },
    }),

    rollingAlpha: builder.mutation({
      query: (payload) => ({
        url: `/api/fund-card/rolling-alpha`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => {
        return parseRollingAlphaResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseFundCardErrorResponse(error);
      },
    }),

    //Asset Allocation
    assetAllocation: builder.mutation({
      query: (payload) => ({
        url: `/api/fund-card/asset-allocation`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => {
        return parseAssetAllocationResponse(response);
      },
    }),

    /**
     * Get manager list
     * POST /api/mutual-funds/manager-list
     *
     * @purpose Retrieve list of fund managers with search capability
     * @useCase Display fund managers in filter dropdown
     *
     * @request {Object} payload
     * @request {string} payload.search - Search term for filtering managers (optional)
     *
     * @response {Object}
     * @response {string} code - Response code (e.g., "MFDS0011")
     * @response {string} message - Success message
     * @response {Array} result - Array of manager objects
     * @response {number} result[].id - Manager ID
     * @response {string} result[].name - Manager name
     */
    getManagerList: builder.mutation({
      query: (payload = {}) => ({
        url: "/api/mutual-funds/manager-list",
        method: "POST",
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response) => {
        return parseManagerListResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseMutualFundsErrorResponse(error);
      },
    }),

    /**
     * Get AMC list
     * POST /api/mutual-funds/amc-list
     *
     * @purpose Retrieve list of AMCs (fund houses) with search capability
     * @useCase Display fund houses in filter dropdown
     *
     * @request {Object} payload
     * @request {string} payload.search - Search term for filtering AMCs (optional)
     *
     * @response {Object}
     * @response {string} code - Response code (e.g., "MFDS0010")
     * @response {string} message - Success message
     * @response {Array} result - Array of AMC objects
     * @response {number} result[].id - AMC ID
     * @response {string} result[].name - AMC name
     */
    getAmcList: builder.mutation({
      query: (payload = {}) => ({
        url: "/api/mutual-funds/amc-list",
        method: "POST",
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response) => {
        return parseAmcListResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseMutualFundsErrorResponse(error);
      },
    }),
    /**
     * Get category list
     * POST /api/mutual-funds/category-list
     *
     * @purpose Retrieve list of categories and subcategories
     * @useCase Display category filters
     *
     * @request {Object} payload
     * @request {string} payload.search - Search term (optional)
     *
     * @response {Object} parsed category list
     */
    getCategoryList: builder.mutation({
      query: (payload = {}) => ({
        url: "/api/mutual-funds/category-list",
        method: "POST",
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response) => {
        return parseCategoryListResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseMutualFundsErrorResponse(error);
      },
    }),

    /**
     * Get fund managers history
     * POST /api/fund-card/fund-managers-history
     */
    getFundManagersHistory: builder.mutation({
      query: (payload) => ({
        url: "/api/fund-card/fund-managers-history",
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => {
        return parseFundManagersHistoryResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseFundCardErrorResponse(error);
      },
      providesTags: ["MutualFundsHistory"],
    }),

    /**
     * Update customer type (experience selection)
     * PATCH /api/customer/profile
     *
     * @purpose Set the customer's fund experience preference
     * @useCase Called after the user selects No-fee (REGULAR) or Fee-based (DIRECT)
     *          in the ChooseExperienceModal after signup
     *
     * @request {Object} payload
     * @request {string} payload.customerType - "REGULAR" | "DIRECT"
     *
     * @response {Object}
     * @response {boolean} success - Whether the update succeeded
     * @response {string} message - Response message
     */
    updateCustomerType: builder.mutation({
      query: (payload) => ({
        url: "/api/customer/profile",
        method: "PATCH",
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response) => ({
        success: response?.success !== false,
        message: response?.message || "Customer type updated successfully",
        data: response?.result || response?.data || null,
      }),
      transformErrorResponse: (error) => parseMutualFundsErrorResponse(error),
    }),

    /**
     * Get SWP parameters for a fund
     * GET /api/mutual-funds/swp/{fundId}
     *
     * @purpose Retrieve SWP configuration parameters for a specific fund
     * @useCase Dynamically populate SWP side drawer with allowed frequencies, dates, and limits
     *
     * @param {string} fundId - ISIN or Scheme ID
     *
     * @response {Object} result - Fund SWP details including swpParams array
     */
    getSwpParams: builder.query({
      query: (fundId) => ({
        url: `/api/mutual-funds/swp/${fundId}`,
        method: "GET",
      }),
      transformResponse: (response) => {
        return parseSwpParamsResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseMutualFundsErrorResponse(error);
      },
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetMutualFundByIdQuery,
  useGetMutualFundCollectionsQuery,
  useGetPopularFundsQuery,
  useGetMutualFundsMutation,
  useGetMutualFundHoldingsQuery,
  useGetMutualFundHistoryQuery,
  useGetRiskProfileFundsMutation,
  useGetRecommendationFundsMutation,
  useConfirmBseOnboardingMutation,
  useGetRiskReturnMatrixMutation,
  useGetSectorAllocationMutation,
  useGetHoldingsMutation,
  useGetRiskReturnStatisticsMutation,
  useMetricsOverviewMutation,
  useEquityAnalysisMutation,
  useRiskRatiosMutation,
  useRiskAdjustedReturnsMutation,
  useRollingAlphaMutation,
  useAssetAllocationMutation,
  useGetManagerListMutation,
  useGetAmcListMutation,
  useGetCategoryListMutation,
  useGetFundManagersHistoryMutation,
  useUpdateCustomerTypeMutation,
  useGetSwpParamsQuery,
} = mutualFundsApi;
